import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Moon, Star, Trophy, Calendar, HelpCircle, Sparkles, Award, Medal, Gift } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/lib/api';

// Prize data
const prizes = [
  { rank: 1, amount: 1000, icon: Trophy, color: 'from-yellow-400 to-yellow-600', textColor: 'text-yellow-400' },
  { rank: 2, amount: 800, icon: Medal, color: 'from-gray-300 to-gray-500', textColor: 'text-gray-300' },
  { rank: 3, amount: 700, icon: Award, color: 'from-amber-500 to-amber-700', textColor: 'text-amber-500' },
  { rank: 4, amount: 600, icon: Gift, color: 'from-emerald-400 to-emerald-600', textColor: 'text-emerald-400' },
  { rank: 5, amount: 500, icon: Gift, color: 'from-blue-400 to-blue-600', textColor: 'text-blue-400' },
  { rank: 6, amount: 400, icon: Gift, color: 'from-purple-400 to-purple-600', textColor: 'text-purple-400' },
  { rank: 7, amount: 300, icon: Gift, color: 'from-pink-400 to-pink-600', textColor: 'text-pink-400' },
  { rank: 8, amount: 200, icon: Gift, color: 'from-cyan-400 to-cyan-600', textColor: 'text-cyan-400' },
];

const totalPrizes = prizes.reduce((sum, p) => sum + p.amount, 0);

export default function Home() {
  const { isAuthenticated } = useAuth();
  const [registrationOpen, setRegistrationOpen] = useState(false);

  useEffect(() => {
    api.getRegistrationStatus().then((res) => {
      if (res.success && res.data) {
        setRegistrationOpen(res.data.open);
      }
    }).catch(() => {
      setRegistrationOpen(false);
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-cream relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 pattern-overlay opacity-30" />
      
      {/* Floating Stars */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <Star
            key={i}
            className="absolute text-gold-500/30 animate-twinkle"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              width: `${8 + Math.random() * 12}px`,
            }}
          />
        ))}
      </div>

      {/* Lanterns */}
      <div className="absolute top-20 left-10 animate-float">
        <div className="w-8 h-16 bg-gradient-to-b from-gold-500 to-gold-700 rounded-b-full opacity-60" />
      </div>
      <div className="absolute top-32 right-16 animate-float" style={{ animationDelay: '2s' }}>
        <div className="w-6 h-12 bg-gradient-to-b from-gold-500 to-gold-700 rounded-b-full opacity-60" />
      </div>

      {/* Hero Section */}
      <section className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-20">
        <div className="text-center max-w-4xl mx-auto">
          {/* Crescent Moon */}
          <Moon className="w-20 h-20 text-gold-500 mx-auto mb-6 animate-pulse-slow" />
          
          {/* Title */}
          <h1 className="text-5xl md:text-7xl font-jazeera font-bold text-gradient-gold mb-4">
            رحلة عبر الزمن
          </h1>
          
          <p className="text-2xl md:text-3xl text-cream/90 mb-2 font-jazeera">
            مسابقة السيرة النبوية الشريفة
          </p>
          
          <p className="text-lg text-gray-400 mb-4 max-w-2xl mx-auto">
            انطلق في رحلة تفاعلية مشوقة عبر محطات السيرة النبوية العطرة،
            واكتشف أسرار التاريخ الإسلامي من خلال ألغاز بصرية فريدة
          </p>

          {/* Sponsor Badge */}
          <div className="inline-flex items-center gap-2 bg-emerald-900/30 border border-emerald-700/50 rounded-full px-6 py-2 mb-8">
            <span className="text-emerald-400 font-bold">برعاية</span>
            <span className="text-cream font-bold">شباب مسجد السلام - عبلين</span>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            {isAuthenticated ? (
              <Link to="/competition" className="btn-primary flex items-center justify-center gap-2">
                <Sparkles className="w-5 h-5" />
                ابدأ المسابقة
              </Link>
            ) : (
              <>
                {registrationOpen && (
                  <Link to="/register" className="btn-primary flex items-center justify-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    سجل الآن
                  </Link>
                )}
                <Link to="/login" className={registrationOpen ? "btn-secondary" : "btn-primary flex items-center justify-center gap-2"}>
                  تسجيل الدخول
                </Link>
              </>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 max-w-lg mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-gold-500/10 border border-gold-500/30 flex items-center justify-center">
                <Trophy className="w-8 h-8 text-gold-500" />
              </div>
              <p className="text-2xl font-bold text-gold-500">{totalPrizes.toLocaleString()}</p>
              <p className="text-sm text-gray-400">شيكل جوائز</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-gold-500/10 border border-gold-500/30 flex items-center justify-center">
                <HelpCircle className="w-8 h-8 text-gold-500" />
              </div>
              <p className="text-2xl font-bold text-gold-500">20</p>
              <p className="text-sm text-gray-400">لغزاً</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-gold-500/10 border border-gold-500/30 flex items-center justify-center">
                <Calendar className="w-8 h-8 text-gold-500" />
              </div>
              <p className="text-2xl font-bold text-gold-500">20</p>
              <p className="text-sm text-gray-400">يوماً</p>
            </div>
          </div>
        </div>
      </section>

      {/* Prizes Section */}
      <section className="relative z-10 py-20 px-4 bg-gradient-to-b from-transparent via-gold-500/5 to-transparent">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Trophy className="w-16 h-16 text-gold-500 mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-jazeera font-bold text-gold-500 mb-2">
              جوائز قيمة
            </h2>
            <p className="text-gray-400">
              مجموع الجوائز: <span className="text-gold-500 font-bold text-2xl">{totalPrizes.toLocaleString()}</span> شيكل
            </p>
          </div>
          
          {/* Top 3 Prizes - Featured */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {prizes.slice(0, 3).map((prize) => {
              const Icon = prize.icon;
              return (
                <div
                  key={prize.rank}
                  className={`card border border-gray-700 text-center relative overflow-hidden ${
                    prize.rank === 1 ? 'md:scale-110 md:-translate-y-4' : ''
                  }`}
                >
                  {/* Glow Effect */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${prize.color} opacity-10`} />
                  
                  <div className="relative z-10">
                    <div className={`w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br ${prize.color} flex items-center justify-center shadow-lg`}>
                      <Icon className="w-10 h-10 text-black" />
                    </div>
                    <p className={`text-lg font-bold ${prize.textColor} mb-1`}>
                      المركز {prize.rank === 1 ? 'الأول' : prize.rank === 2 ? 'الثاني' : 'الثالث'}
                    </p>
                    <p className="text-4xl font-bold text-cream mb-1">
                      {prize.amount.toLocaleString()}
                    </p>
                    <p className="text-gray-400">شيكل</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Remaining Prizes */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {prizes.slice(3).map((prize) => {
              const Icon = prize.icon;
              return (
                <div
                  key={prize.rank}
                  className="card border border-gray-800 text-center py-4"
                >
                  <div className={`w-12 h-12 mx-auto mb-2 rounded-full bg-gradient-to-br ${prize.color} flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-black" />
                  </div>
                  <p className={`text-sm font-bold ${prize.textColor}`}>
                    المركز {prize.rank === 4 ? 'الرابع' : prize.rank === 5 ? 'الخامس' : prize.rank === 6 ? 'السادس' : prize.rank === 7 ? 'السابع' : 'الثامن'}
                  </p>
                  <p className="text-2xl font-bold text-cream">
                    {prize.amount}
                  </p>
                  <p className="text-xs text-gray-500">شيكل</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="relative z-10 py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-jazeera font-bold text-center text-gold-500 mb-12">
            كيف تشارك؟
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: 1, title: 'سجل مبكراً', desc: 'أنشئ حسابك قبل بداية رمضان' },
              { step: 2, title: 'حل الألغاز', desc: 'أجب على سؤال جديد كل يوم' },
              { step: 3, title: 'اربح الجوائز', desc: 'تنافس واحصد النقاط' },
            ].map((item) => (
              <div key={item.step} className="card text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gold-500 text-black flex items-center justify-center text-xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-cream mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="relative z-10 py-20 px-4 bg-gray-900/30">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-jazeera font-bold text-gold-500 mb-6">
            عن المسابقة
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed">
            رحلة عبر الزمن هي مسابقة تفاعلية فريدة تأخذك في جولة مشوقة عبر أحداث السيرة النبوية الشريفة.
            كل يوم من أيام رمضان المبارك، ستواجه لغزاً بصرياً جديداً يتعلق بحدث من أحداث حياة النبي محمد ﷺ.
            اكتشف المحطات الخمس واجمع النقاط لتتصدر قائمة المتسابقين!
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-8 px-4 border-t border-gray-800">
        <div className="max-w-4xl mx-auto text-center space-y-4">
          <div className="flex items-center justify-center gap-2">
            <span className="text-emerald-400 font-bold">برعاية</span>
            <span className="text-cream font-bold text-lg">شباب مسجد السلام - عبلين</span>
          </div>
          <p className="text-gray-500 text-sm">
            رحلة عبر الزمن - مسابقة السيرة النبوية © رمضان {new Date().getFullYear()}
          </p>
        </div>
      </footer>
    </div>
  );
}
